import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock, AlertTriangle, Sparkles, UserPen, Pencil, ExternalLink, Plus, ShieldCheck, CalendarPlus, Link, X, FileText } from 'lucide-react';
import { MilestoneData } from '../lib/types';
const statusConfig = {
  completed: {
    icon: CheckCircle2,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    lineColor: 'border-emerald-300'
  },
  overdue: {
    icon: AlertTriangle,
    color: 'text-red-500',
    bg: 'bg-red-50',
    lineColor: 'border-red-300'
  },
  upcoming: {
    icon: Clock,
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    lineColor: 'border-border'
  }
};
const sourceConfig = {
  'ai-extracted': {
    label: 'AI Extract',
    icon: Sparkles,
    cls: 'bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200'
  },
  manual: {
    label: 'Manual',
    icon: UserPen,
    cls: 'bg-slate-50 text-slate-500 hover:bg-slate-50 border border-slate-200'
  },
  policy: {
    label: 'Policy',
    icon: ShieldCheck,
    cls: 'bg-sky-50 text-sky-700 hover:bg-sky-50 border border-sky-200'
  }
};
interface MilestonesTabProps {
  milestones: MilestoneData[];
}
export function MilestonesTab({
  milestones
}: MilestonesTabProps) {
  const [showCalendarMenu, setShowCalendarMenu] = useState(false);
  const [showSourceViewer, setShowSourceViewer] = useState(false);
  const calendarMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (calendarMenuRef.current && !calendarMenuRef.current.contains(e.target as Node)) {
        setShowCalendarMenu(false);
      }
    }
    if (showCalendarMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendarMenu]);
  return <Card className="border-0 shadow-sm" data-id="element-340">
      <CardContent className="p-6" data-id="element-341">
        <div className="relative" data-id="element-342">
          {/* Buttons — top-right, aligned with first entry */}
          <div className="absolute top-0 right-0 z-10 flex items-center gap-2" data-id="element-343">
            <div className="relative" ref={calendarMenuRef} data-id="element-344">
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => setShowCalendarMenu(!showCalendarMenu)} data-id="element-345">
                <CalendarPlus className="h-3.5 w-3.5" data-id="element-346" />
                Add to Calendar
              </Button>
              {showCalendarMenu && <div className="absolute top-full right-0 mt-1 w-64 rounded-md bg-white border shadow-lg py-1 z-50" data-id="element-347">
                  <button onClick={() => setShowCalendarMenu(false)} className="flex items-center gap-2.5 w-full px-3 py-2 text-xs text-foreground hover:bg-muted transition-colors whitespace-nowrap" data-id="element-348">
                    <img src="https://cdn.magicpatterns.com/uploads/2s3GeBu8WcKDofKs3v2LFe/microsoft-outlook-icon.png" alt="Outlook" className="h-4 w-4 flex-shrink-0 object-contain" data-id="element-349" />
                    Sync with Outlook
                  </button>
                  <button onClick={() => setShowCalendarMenu(false)} className="flex items-center gap-2.5 w-full px-3 py-2 text-xs text-foreground hover:bg-muted transition-colors whitespace-nowrap" data-id="element-350">
                    <img src="https://cdn.magicpatterns.com/uploads/47N9hoVvHJm93zMHiBEdcH/google-calendar-icon.png" alt="Google Calendar" className="h-4 w-4 flex-shrink-0 object-contain" data-id="element-351" />
                    Sync with Google Calendar
                  </button>
                </div>}
            </div>
            <Button size="sm" className="h-8 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700" data-id="element-352">
              <Plus className="h-3.5 w-3.5" data-id="element-353" />
              Add Milestone
            </Button>
          </div>

          {milestones.map((milestone, index) => {
          const config = statusConfig[milestone.status];
          const Icon = config.icon;
          const isLast = index === milestones.length - 1;
          const srcConfig = sourceConfig[milestone.source];
          const SrcIcon = srcConfig.icon;
          return <div key={milestone.id} className="flex gap-4 relative" data-id="element-354">
                {/* Timeline line */}
                {!isLast && <div className={`absolute left-[15px] top-[32px] w-px h-[calc(100%-8px)] border-l-2 border-dashed ${config.lineColor}`} data-id="element-355" />}

                {/* Icon */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full ${config.bg} flex items-center justify-center z-10`} data-id="element-356">
                  <Icon className={`h-4 w-4 ${config.color}`} data-id="element-357" />
                </div>

                {/* Content */}
                <div className={`flex-1 pb-6 ${isLast ? 'pb-0' : ''}`} data-id="element-358">
                  {/* Title row */}
                  <div className="flex flex-wrap items-center gap-2 mb-0.5" data-id="element-359">
                    <span className="text-sm font-semibold text-foreground" data-id="element-360">
                      {milestone.title}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${milestone.type === 'external' ? 'text-indigo-600 bg-indigo-50' : 'text-muted-foreground bg-muted'}`} data-id="element-361">
                      {milestone.type === 'external' ? 'External' : 'Internal'}
                    </span>
                    {milestone.status === 'overdue' && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-0 text-[10px] px-1.5 py-0 h-5" data-id="element-362">
                        Overdue
                      </Badge>}
                  </div>

                  {/* Date */}
                  <div className="text-xs text-muted-foreground" data-id="element-363">
                    {milestone.date}
                  </div>

                  {/* Note */}
                  {milestone.note && <p className="text-xs text-muted-foreground mt-1 italic" data-id="element-364">
                      {milestone.note}
                    </p>}

                  {/* Bottom row: source badge + source link + edit */}
                  <div className="flex items-center gap-2 mt-2" data-id="element-365">
                    <Badge className={`${srcConfig.cls} text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none`} data-id="element-366">
                      <SrcIcon className="h-3 w-3" data-id="element-367" />
                      {srcConfig.label}
                    </Badge>

                    {milestone.source === 'ai-extracted' && milestone.sourceRef && <button onClick={() => {
                  if (milestone.sourceRef?.includes('Letter of Intent')) {
                    setShowSourceViewer(true);
                  }
                }} className="inline-flex items-center gap-1 text-[11px] text-violet-600 hover:text-violet-800 hover:underline font-medium transition-colors" data-id="element-368">
                          <ExternalLink className="h-3 w-3" data-id="element-369" />
                          {milestone.sourceRef}
                        </button>}

                    {milestone.source !== 'policy' && <button className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground font-medium ml-1 transition-colors" data-id="element-370">
                        <Pencil className="h-3 w-3" data-id="element-371" />
                        Edit
                      </button>}
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Source Document Viewer Modal */}
        {showSourceViewer && <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-372">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowSourceViewer(false)} data-id="element-373" />
            <div className="relative z-10 w-full max-w-3xl max-h-[85vh] bg-white rounded-lg shadow-2xl border flex flex-col mx-4" data-id="element-374">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-border flex-shrink-0" data-id="element-375">
                <div className="flex items-center gap-2.5" data-id="element-376">
                  <div className="p-1.5 rounded-md bg-violet-50 border border-violet-100" data-id="element-377">
                    <FileText className="h-4 w-4 text-violet-600" data-id="element-378" />
                  </div>
                  <div data-id="element-379">
                    <h3 className="text-sm font-semibold text-foreground" data-id="element-380">
                      Source Document
                    </h3>
                    <p className="text-[11px] text-muted-foreground" data-id="element-381">
                      RFA-EY-13-001 — Key Dates section
                    </p>
                  </div>
                  <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-0.5 shadow-none font-medium ml-1" data-id="element-382">
                    <Sparkles className="h-3 w-3" data-id="element-383" />
                    AI Extracted
                  </Badge>
                </div>
                <button onClick={() => setShowSourceViewer(false)} className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" data-id="element-384">
                  <X className="h-4 w-4" data-id="element-385" />
                </button>
              </div>

              {/* Modal Body — scrollable image */}
              <div className="flex-1 overflow-auto p-4 bg-muted/30" data-id="element-386">
                <div className="rounded-md border border-border shadow-sm overflow-hidden bg-white" data-id="element-387">
                  <img src="https://cdn.magicpatterns.com/uploads/t8298arfS2GxMbrzJ27Tti/ChatGPT_Image_Feb_17,_2026,_05_07_43_PM.png" alt="FOA Key Dates — Letter of Intent Due Date highlighted" className="w-full h-auto" data-id="element-388" />
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-border bg-muted/20 rounded-b-lg flex items-center justify-between flex-shrink-0" data-id="element-389">
                
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowSourceViewer(false)} data-id="element-390">
                  Close
                </Button>
              </div>
            </div>
          </div>}
      </CardContent>
    </Card>;
}